/**
 * ProjectLogModal — per-project MCP call log for Codable.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Activity, Check, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, EmptyState, Modal } from '../../../components/ui';

interface ProjectLogCall {
  id: string;
  tool_name: string;
  raw_tool_name?: string | null;
  action?: string | null;
  actor_entity_id?: string | null;
  actor_key_preview?: string | null;
  actor_key_label?: string | null;
  actor_entity_label?: string | null;
  actor_display?: string | null;
  args_summary?: unknown;
  result_summary?: unknown;
  ok: boolean;
  error?: string | null;
  duration_ms: number;
  created_at: string;
}

interface ProjectLogResp {
  ok: boolean;
  calls?: ProjectLogCall[];
  error?: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function actorLabel(call: ProjectLogCall, unknown: string): string {
  const who = call.actor_display ?? call.actor_entity_label ?? call.actor_entity_id ?? unknown;
  const token = call.actor_key_label
    ? call.actor_key_preview
      ? `${call.actor_key_label} (${call.actor_key_preview}…)`
      : call.actor_key_label
    : call.actor_key_preview
    ? `${call.actor_key_preview}…`
    : null;
  return token ? `${who} · ${token}` : who;
}

function shortJson(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  try {
    const s = JSON.stringify(value);
    return s.length > 180 ? `${s.slice(0, 180)}...` : s;
  } catch {
    return '';
  }
}

export default function ProjectLogModal(
  { slug, onClose, onBack }: { slug: string; onClose: () => void; onBack?: () => void },
) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['codable-project-log', slug],
    queryFn: () => api.mcp<ProjectLogResp>('codable.project_log', { slug, limit: 120 }),
  });

  const calls = data?.calls ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      onBack={onBack}
      backLabel={t('codable.wsSettings')}
      title={t('codable.logTitle')}
      size='xl'
    >
      <div className='space-y-1 max-h-[65vh] overflow-y-auto -m-1 p-1'>
        {isLoading
          ? (
            <div className='space-y-2 py-2' aria-hidden='true'>
              {[...Array(6)].map((_, i) => (
                <div key={i} className='h-12 rounded-xl bg-gray-800 animate-pulse' />
              ))}
            </div>
          )
          : data?.ok === false
          ? <div className='text-sm text-red-400 py-2'>{data.error ?? t('codable.logLoadError')}</div>
          : calls.length === 0
          ? (
            <EmptyState
              icon={Activity}
              title={t('codable.logEmpty')}
              description={t('codable.logEmptyDesc')}
            />
          )
          : calls.map((call) => {
            const args = shortJson(call.args_summary);
            const result = shortJson(call.result_summary);
            return (
              <div
                key={call.id}
                className='rounded-xl border border-gray-800 bg-gray-900/35 px-3 py-2.5'
              >
                <div className='flex items-start gap-3'>
                  <div
                    className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                      call.ok
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-red-500/10 text-red-300'
                    }`}
                  >
                    {call.ok ? <Check size={13} /> : <X size={13} />}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='font-mono text-xs text-white truncate'>{call.tool_name}</span>
                      {call.action && <Badge variant='blue'>{call.action}</Badge>}
                      <Badge variant={call.ok ? 'green' : 'red'}>{call.ok ? 'ok' : 'fail'}</Badge>
                    </div>
                    <div className='mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-3 gap-y-1'>
                      <span>{formatTime(call.created_at)}</span>
                      <span>{actorLabel(call, t('codable.logUnknownCaller'))}</span>
                      <span>{call.duration_ms} ms</span>
                    </div>
                    {call.error && (
                      <div className='mt-1 text-xs text-red-300 truncate'>{call.error}</div>
                    )}
                    {(args || result) && (
                      <div className='mt-1 grid grid-cols-1 lg:grid-cols-2 gap-2 text-[11px] font-mono text-gray-500'>
                        {args && <div className='truncate'>args {args}</div>}
                        {result && <div className='truncate'>result {result}</div>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </Modal>
  );
}
